import { useState, useContext, useEffect, Fragment } from "react"
import { useQuery } from "@apollo/client"
import { Formik, Form as FormWrapper } from "formik"
import styles from "../../../styles/Form.module.css"
import { v4 as uuidv4 } from "uuid"

// Context
import { UserContext } from "../../../context/userContext"

// Queries
import { GET_PARTICIPANT, PARTICIPANT_BLOCK } from "./queries"
import { telerecoveryApiUrl } from "../../../config/microservicesUrls"

// Helpers
import validationSchema from "../../../helpers/validationSchema/"
import initialValues from "../../../helpers/initialValues"
import {
  normalizePayload,
  transformPayload,
} from "../../../helpers/dateNormalization"
import { axiosPost } from "../../../helpers/microserviceAPIs"

// Comps
import FieldBuilder from "./FieldBuilder"
import { LoaderMedium } from "../../../components/Loaders"
import { Button } from "primereact/button"
import { LogError } from "../../../helpers/logger"
import { Tooltip } from "primereact/tooltip"
import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"

const Form = ({
  blockName,
  className,
  organization_id,
  organization_alias,
  enterprise_id,
  enterprise_alias,
}) => {
  const [fetchData, setFetchData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(false)
  const userCtx = useContext(UserContext)
  const participant_id = userCtx.participant_id
  const participant_alias = userCtx.participant_alias
  const [participant_membership_id, setParticipantMembershipId] = useState(null)
  const [formId] = useState(() => uuidv4())

  const { refetch: refetchBlock } = useQuery(PARTICIPANT_BLOCK, {
    fetchPolicy: "network-only",
    variables: {
      type: "auto",
      participant_id,
      idBlock: blockName,
      organization_id,
      enterprise_id,
    },
    skip: true,
  })

  const { refetch: refetchParticipant } = useQuery(GET_PARTICIPANT, {
    fetchPolicy: "network-only",
    variables: {
      participant_id: userCtx.participant_id,
      organization_id: organization_id,
    },
    skip: !userCtx.participant_id || !organization_id,
  })

  function ProcessBlockData(data) {
    let _fetchData = structuredClone(data)

    if (_fetchData.blockSaved?.data && _fetchData.block) {
      try {
        // Try to transform the payload, fall back to raw values if transformation fails for any reason
        const parsedBlockSavedData = JSON.parse(_fetchData.blockSaved.data)
        const rawValues = parsedBlockSavedData?.values ?? {}
        const transformedValues =
          transformPayload(rawValues, [_fetchData.block]) ?? rawValues

        _fetchData.blockSavedTransformed = {
          ...parsedBlockSavedData,
          values: transformedValues,
        }
      } catch (error) {
        LogError("Error transforming block data:", error)
      }
    }

    setFetchData(_fetchData)
  }

  useEffect(() => {
    setLoading(true)

    // Fetch participant information
    try {
      refetchParticipant()
        .then(({ data }) => {
          setParticipantMembershipId(
            data?.participant?.participant_membership_id,
          )
        })
        .catch((error) => {
          LogError("Error fetching participant data:", error)
        })

      // Fetch block data
      refetchBlock()
        .then(({ data }) => {
          ProcessBlockData(data)
        })
        .catch((error) => {
          LogError("Error fetching block data:", error)
        })
        .finally(() => {
          setLoading(false)
        })
    } catch (error) {
      LogError("Error fetching data:", error)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (err) return <div>Error saving data...</div>
  if (loading || !fetchData) return <LoaderMedium />
  if (err) return <div>Error saving data...</div>
  if (loading || !fetchData) return <LoaderMedium />

  const dataStored = fetchData.blockSavedTransformed
    ? fetchData.blockSavedTransformed
    : fetchData.blockSaved
      ? JSON.parse(fetchData.blockSaved.data)
      : {}

  const formValues = {
    ...initialValues(fetchData?.block),
    ...(dataStored?.values || {}),
  }

  const handleSubmit = async (values, actions) => {
    setLoading(true)

    try {
      const normalizedValues =
        normalizePayload(values, [fetchData.block]) ?? values

      const postBody = {
        idDoc: dataStored.idDoc,
        organization_alias,
        organization_id,
        enterprise_id,
        enterprise_alias,
        participant_membership_id,
        participant_alias,
        participant_id,
        staff_alias: 0,
        staff_id: 0,
        form_id: formId,
        block_name: blockName,
        values: normalizedValues,
      }

      const response = await callCloudFunction(
        "blockSubmitParticipant",
        postBody,
        userCtx.tokens.getMainToken(),
      )

      if (response.status === 200) {
        // Clean up staged submission after successful submission/edit
        try {
          await callCloudFunction(
            "deleteStagedFormSubmission",
            { form_id: formId },
            userCtx.tokens.getMainToken(),
          )
        } catch (cleanupError) {
          LogError("Error cleaning up staged submission:", cleanupError)
        }

        if (blockName === "participant__contact_information") {
          let newUserPhoneNumber = values?.participant_phone_primary
          await createHNR(newUserPhoneNumber)
        }
        userCtx.refetchParticipantData()
        refetchBlock()
          .then(({ data }) => {
            ProcessBlockData(data)
          })
          .catch((error) => {
            LogError("Error fetching data:", error)
          })
          .finally(() => {
            setLoading(false)
          })
      } else {
        LogError("Error saving data")
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      setErr(true)
    } finally {
      actions.setSubmitting(false)
    }
  }

  async function createHNR(newPhone) {
    try {
      if (newPhone && newPhone !== userCtx.participant_phone_primary) {
        const removeHnrUrl = `${telerecoveryApiUrl()}/Numbers/RemoveHnrSOAR?caseId=${participant_id}&organizationId=${organization_id}`
        const createHnrUrl = `${telerecoveryApiUrl()}/Numbers/CreateHnr`
        const createHnrBody = {
          phoneNumber: newPhone,
          caseUserId: participant_id,
          organizationId: organization_id,
        }

        await axiosPost(
          userCtx.tokens.getTelerecoveryToken(),
          removeHnrUrl,
          null,
        )
        await axiosPost(
          userCtx.tokens.getTelerecoveryToken(),
          createHnrUrl,
          createHnrBody,
        )
      }
    } catch (error) {
      LogError("Error during HNR update process:", error)
    }
  }

  const renderForm = (touched, errors) => {
    return (
      <div className={className || "flex flex-column gap-3"}>
        {fetchData.block.fields.map((field, index) => (
          <Fragment key={field.idField}>
            <FieldBuilder
              name={field.field_name}
              field={field}
              index={index}
              block={blockName}
              className={styles.group_container}
              enterprise_id={enterprise_id}
              organization_id={organization_id}
            />
          </Fragment>
        ))}
      </div>
    )
  }

  return (
    <Formik
      key={blockName}
      initialValues={formValues}
      validationSchema={validationSchema(fetchData.block)}
      onSubmit={async (values, actions) => {
        await handleSubmit(values, actions)
      }}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <FormWrapper>
          {renderForm(touched, errors)}
          <div className={styles.form_actions}>
            {Object.keys(errors)?.length > 0 && (
              <i
                id={`${blockName}-save-info`}
                className="pi pi-exclamation-circle"
              />
            )}
            <Button
              type="submit"
              label="Save"
              className="p-button-rounded"
              style={{ minWidth: "80px" }}
              disabled={isSubmitting || !isValid}
            />
          </div>
          <Tooltip
            className={styles.errorTooltip}
            target={`#${blockName}-save-info`}
            position="left"
            my="right bottom"
            showDelay={100}
          >
            {Object.keys(errors)?.length > 0 && (
              <div>
                Please fix the following form errors in order to save:
                <ul>
                  {Object.keys(errors)
                    .reverse()
                    .map((key) => {
                      // Find field label
                      let fieldLabel = key
                      const field = fetchData.block.fields.find(
                        (f) => f.field_name === key,
                      )
                      if (field) fieldLabel = field.field_label

                      return (
                        <li className={styles.errorItem} key={key}>
                          {fieldLabel}: {errors[key]}
                        </li>
                      )
                    })}
                </ul>
              </div>
            )}
          </Tooltip>
        </FormWrapper>
      )}
    </Formik>
  )
}
export default Form
